import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createSession, sendChat, checkAIHealth, bookingChat, conciergeChat, managerChat } from '../services/aiService';
import './ChatBubble.css';

// ── Simple inline markdown → JSX renderer ─────────────────────────────────────
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const result = [];
  let listBuffer = [];
  let key = 0;

  const flushList = () => {
    if (listBuffer.length) {
      result.push(
        <ul key={key++}>
          {listBuffer.map((item, i) => <li key={i}>{parseLine(item)}</li>)}
        </ul>
      );
      listBuffer = [];
    }
  };

  const parseLine = (line) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      /^\*\*(.+)\*\*$/.test(p)
        ? <strong key={i}>{p.slice(2, -2)}</strong>
        : p
    );
  };

  lines.forEach((line) => {
    const trimmed = line.trimStart();
    if (trimmed.startsWith('• ') || trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      listBuffer.push(trimmed.slice(2));
    } else {
      flushList();
      if (line.trim() === '') {
        result.push(<br key={key++} />);
      } else if (/^#{1,3} /.test(line)) {
        const level = line.match(/^(#{1,3}) /)[1].length;
        const content = line.replace(/^#{1,3} /, '');
        const Tag = `h${level + 2}`;
        result.push(<Tag key={key++} className="cb-heading">{parseLine(content)}</Tag>);
      } else {
        result.push(<p key={key++}>{parseLine(line)}</p>);
      }
    }
  });
  flushList();
  return result;
}

/**
 * Pick the booking that best represents the guest's current stay so the
 * concierge agent can call service-request tools with real IDs.
 *
 * Priority:
 *   1. Currently checked-in (status === 'checked_in')
 *   2. Confirmed/pending and today is between check-in and check-out
 *   3. Soonest upcoming confirmed/pending booking
 *   4. null if none match
 */
function pickActiveBooking(bookings) {
  if (!Array.isArray(bookings) || bookings.length === 0) return null;
  const now = Date.now();
  const dayMs = 86_400_000;

  const checkedIn = bookings.find((b) => b.status === 'checked_in');
  if (checkedIn) return checkedIn;

  const inWindow = bookings.find((b) => {
    if (!['confirmed', 'pending'].includes(b.status)) return false;
    const ci = b.check_in_date && new Date(b.check_in_date).getTime();
    const co = b.check_out_date && new Date(b.check_out_date).getTime();
    if (!ci || !co) return false;
    return ci - dayMs <= now && now <= co + dayMs;
  });
  if (inWindow) return inWindow;

  const upcoming = bookings
    .filter((b) => ['confirmed', 'pending'].includes(b.status))
    .filter((b) => b.check_in_date && new Date(b.check_in_date).getTime() >= now)
    .sort((a, b) => new Date(a.check_in_date) - new Date(b.check_in_date));
  return upcoming[0] || null;
}

const AGENT_META = {
  booking:   { name: 'Innio',  color: '#6366f1', emoji: '🏨' },
  concierge: { name: 'Aria',   color: '#10b981', emoji: '🛎️' },
  manager:   { name: 'NOVA',   color: '#f59e0b', emoji: '⚙️' },
  auto:      { name: 'Innio',  color: '#6366f1', emoji: '🏨' },
};

export default function ChatBubble() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen]               = useState(false);
  const [messages, setMessages]       = useState([]);
  const [input, setInput]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [sessionId, setSessionId]     = useState(null);
  const [agentType, setAgentType]     = useState('auto');
  const [aiOnline, setAiOnline]       = useState(true);
  const [chatStateLoaded, setChatStateLoaded] = useState(false);
  const [hasRestoredChat, setHasRestoredChat] = useState(false);
  const [unread, setUnread]           = useState(0);
  // Active booking — fetched once for guests so concierge tools have real
  // booking_id / room_id / room_number / hotel_id context. Without this the
  // LLM hallucinates IDs and backend tool calls 400.
  const [activeBooking, setActiveBooking] = useState(null);
  const messagesEndRef  = useRef(null);
  const inputRef        = useRef(null);
  const historyRef      = useRef([]);
  const lastHotelsRef   = useRef(null);

  const getChatStorageKey = useCallback(() => {
    const uid = userProfile?.userId ?? userProfile?.user_id ?? userProfile?.id ?? currentUser?.uid;
    if (!uid) return null;
    return `innbotics_chat_${uid}`;
  }, [userProfile, currentUser]);

  // Restore chat from localStorage
  useEffect(() => {
    const storageKey = getChatStorageKey();
    if (!storageKey) {
      setMessages([]);
      setSessionId(null);
      setAgentType('auto');
      historyRef.current = [];
      setHasRestoredChat(false);
      setChatStateLoaded(false);
      return;
    }
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        setMessages([]);
        setSessionId(null);
        setAgentType('auto');
        historyRef.current = [];
        setHasRestoredChat(false);
        setChatStateLoaded(true);
        return;
      }
      const parsed = JSON.parse(raw);
      const restoredMessages = Array.isArray(parsed?.messages)
        ? parsed.messages.map((msg) => ({
            ...msg,
            timestamp: msg?.timestamp ? new Date(msg.timestamp) : new Date(),
          }))
        : [];
      setMessages(restoredMessages);
      setSessionId(parsed?.sessionId || null);
      setAgentType(parsed?.agentType || 'auto');
      historyRef.current = Array.isArray(parsed?.history) ? parsed.history : [];
      setHasRestoredChat(restoredMessages.length > 0);
    } catch (_) {
      localStorage.removeItem(getChatStorageKey());
      setMessages([]);
      setSessionId(null);
      setAgentType('auto');
      historyRef.current = [];
      setHasRestoredChat(false);
    } finally {
      setChatStateLoaded(true);
    }
  }, [getChatStorageKey]);

  // Persist chat to localStorage
  useEffect(() => {
    if (!chatStateLoaded) return;
    const storageKey = getChatStorageKey();
    if (!storageKey) return;
    const payload = { sessionId, agentType, messages, history: historyRef.current, updatedAt: new Date().toISOString() };
    localStorage.setItem(storageKey, JSON.stringify(payload));
  }, [messages, sessionId, agentType, chatStateLoaded, getChatStorageKey]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [messages, open]);

  // Focus input when panel opens; clear unread badge
  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  const getInitialAgent = useCallback(() => {
    const role = userProfile?.role;
    if (role === 'manager') return 'manager';
    if (role === 'guest' && (activeBooking || userProfile?.hasActiveBooking)) return 'concierge';
    return 'booking';
  }, [userProfile, activeBooking]);

  const buildContext = useCallback(() => {
    const ctx = {};
    const uid = userProfile?.userId ?? userProfile?.user_id ?? userProfile?.id;
    if (uid != null)            ctx.user_id    = String(uid);
    const name = userProfile?.fullName ?? userProfile?.full_name;
    if (name)                   ctx.user_name  = name;
    if (name)                   ctx.guest_name = name;
    const email = userProfile?.email;
    if (email)                  ctx.user_email  = email;
    if (email)                  ctx.guest_email = email;
    const phone = userProfile?.phone ?? userProfile?.phoneNumber ?? userProfile?.phone_number;
    if (phone)                  ctx.guest_phone = phone;
    if (userProfile?.role)      ctx.role       = userProfile.role;
    if (userProfile?.hotel_id)  ctx.hotel_id   = String(userProfile.hotel_id);
    if (userProfile?.booking_id) ctx.booking_id = String(userProfile.booking_id);
    // Real booking context — overrides anything from userProfile because
    // the live bookings API is the source of truth. Without these the
    // concierge agent fabricates IDs and tool calls 400 (see investigation
    // in services.js:/agent/request).
    if (activeBooking) {
      ctx.hotel_id      = String(activeBooking.hotel_id);
      ctx.booking_id    = String(activeBooking.booking_id);
      if (activeBooking.room_id != null)     ctx.room_id     = String(activeBooking.room_id);
      if (activeBooking.room_number != null) ctx.room_number = String(activeBooking.room_number);
      if (activeBooking.hotel_name)          ctx.hotel_name  = activeBooking.hotel_name;
      ctx.is_checked_in = activeBooking.status === 'checked_in';
      ctx.booking_status = activeBooking.status;
    }
    // Location for "nearby hotels" feature
    if (userProfile?.city)      ctx.user_city  = userProfile.city;
    // Recently suggested hotels — lets agent resolve "book this hotel" references
    if (lastHotelsRef.current?.length > 0) {
      ctx.suggested_hotels = JSON.stringify(
        lastHotelsRef.current.map(h => ({
          hotel_id: h.hotel_id,
          name: h.name,
          city: h.city,
          category: h.category ?? null,
        }))
      );
    }
    return ctx;
  }, [userProfile, activeBooking]);

  // Fetch the guest's bookings once on login so the AI session has real
  // booking/room context. Same-origin /api/... goes through the Vercel
  // rewrite to api.innbotics.com.
  useEffect(() => {
    if (!currentUser) { setActiveBooking(null); return; }
    if (userProfile?.role && userProfile.role !== 'guest') { setActiveBooking(null); return; }

    let cancelled = false;
    (async () => {
      try {
        const idToken = await currentUser.getIdToken();
        const resp = await fetch(`/api/guest-bookings/user/${currentUser.uid}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!resp.ok) return;
        const data = await resp.json();
        if (cancelled) return;
        const picked = pickActiveBooking(data?.bookings);
        if (picked) setActiveBooking(picked);
      } catch (_) {
        // Silently fall through — booking-context is best-effort.
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser, userProfile?.role]);

  // Session init + greeting
  useEffect(() => {
    if (!chatStateLoaded) return;
    let cancelled = false;

    async function init() {
      const healthy = await checkAIHealth();
      if (cancelled) return;
      if (!healthy) { setAiOnline(false); return; }

      try {
        if (sessionId) return;
        const agent = getInitialAgent();
        const ctx   = buildContext();
        let token = null;
        try { token = await currentUser?.getIdToken(); } catch (_) {}
        const sess = await createSession(agent, ctx, token);
        if (cancelled) return;
        setSessionId(sess.session_id);
        setAgentType(sess.agent_type);
        if (!hasRestoredChat) {
          const greeting = agent === 'manager'
            ? `Hello ${userProfile?.fullName || 'Manager'}! I'm **NOVA**, your AI Operations Manager. Ask me about staff, tasks, bookings, or hotel status.`
            : agent === 'concierge'
            ? `Welcome back, ${userProfile?.fullName || 'Guest'}! 🛎️ I'm **Aria**, your in-stay assistant. How can I help you today?`
            : `Hi${userProfile?.fullName ? ` ${userProfile.fullName}` : ''}! I'm **Innio**, your hotel booking assistant. I can help you find and book hotels. Where would you like to stay?`;
          setMessages([{ id: Date.now(), role: 'agent', agent: sess.agent_type, content: greeting, timestamp: new Date() }]);
        }
      } catch (_) {
        if (!cancelled) {
          const agent = getInitialAgent();
          setAgentType(agent);
          if (!hasRestoredChat) {
            const greeting = agent === 'concierge'
              ? `Welcome back, ${userProfile?.fullName || 'Guest'}! 🛎️ I'm **Aria**, your in-stay assistant.`
              : `Hi${userProfile?.fullName ? ` ${userProfile.fullName}` : ''}! I'm **Innio**, your hotel booking assistant. Where would you like to stay?`;
            setMessages([{ id: Date.now(), role: 'agent', agent, content: greeting, timestamp: new Date() }]);
          }
        }
      }
    }
    init();
    return () => { cancelled = true; };
  }, [currentUser, userProfile, getInitialAgent, buildContext, sessionId, hasRestoredChat, chatStateLoaded]);

  const directChat = useCallback(async (text, token) => {
    const agent = agentType || getInitialAgent();
    const ctx   = buildContext();
    const hist  = historyRef.current;
    if (agent === 'manager')   return await managerChat(text, hist, ctx, token);
    if (agent === 'concierge') return await conciergeChat(text, hist, ctx, token);
    return await bookingChat(text, hist, ctx, token);
  }, [agentType, getInitialAgent, buildContext]);

  const handleSend = async (optionText = null) => {
    const text = optionText != null ? String(optionText).trim() : input.trim();
    if (!text || loading) return;

    const userMsg = { id: Date.now(), role: 'user', content: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    if (optionText == null) setInput('');
    else setInput('');
    setLoading(true);

    try {
      let token = null;
      try { token = await currentUser?.getIdToken(); } catch (_) {}
      let res;

      if (sessionId) {
        try {
          res = await sendChat(sessionId, text, token);
        } catch (err) {
          if (err.status === 404 || err.message?.toLowerCase().includes('not found')) {
            try {
              const sess = await createSession(getInitialAgent(), buildContext(), token);
              setSessionId(sess.session_id);
              res = await sendChat(sess.session_id, text, token);
            } catch (_) {
              res = await directChat(text, token);
            }
          } else {
            throw err;
          }
        }
      } else {
        res = await directChat(text, token);
      }

      historyRef.current = [
        ...historyRef.current,
        { role: 'user',      content: text },
        { role: 'assistant', content: res.reply },
      ].slice(-20);

      if (res.agent_used) setAgentType(res.agent_used);

      const agentMsg = {
        id: Date.now() + 1,
        role: 'agent',
        agent: res.agent_used || agentType,
        content: res.reply,
        actions: res.actions || [],
        escalation: res.escalation || null,
        hotels: Array.isArray(res.hotels) && res.hotels.length > 0 ? res.hotels : null,
        room_types: Array.isArray(res.room_types) && res.room_types.length > 0 ? res.room_types : null,
        timestamp: new Date(),
      };
      // Keep track of last-shown hotels so context can reference them
      if (agentMsg.hotels) lastHotelsRef.current = agentMsg.hotels;
      setMessages(prev => [...prev, agentMsg]);

      // Badge for unread messages when panel is closed
      if (!open) setUnread(c => c + 1);
    } catch (_) {
      setMessages(prev => [...prev, {
        id: Date.now() + 2,
        role: 'agent',
        agent: 'auto',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      }]);
    } finally {
      setLoading(false);
      if (open) inputRef.current?.focus();
    }
  };

  const meta = AGENT_META[agentType] || AGENT_META.auto;

  return (
    <>
      {/* ── Floating chat panel ────────────────────────────────────────────── */}
      <div className={`cb-panel ${open ? 'cb-panel--open' : ''}`}>
        {/* Panel header */}
        <div className="cb-panel-header" style={{ background: meta.color }}>
          <div className="cb-header-info">
            <span className="cb-header-emoji">{meta.emoji}</span>
            <div>
              <div className="cb-header-name">{meta.name}</div>
              <div className="cb-header-sub">
                <span className="cb-status-dot" style={{ background: aiOnline ? '#86efac' : '#fca5a5' }} />
                {aiOnline ? 'Online' : 'Offline'}
              </div>
            </div>
          </div>
          <button className="cb-close-btn" onClick={() => setOpen(false)} title="Minimize">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Messages */}
        <div className="cb-messages">
          {messages.map(msg => {
            const msgMeta = AGENT_META[msg.agent] || AGENT_META.auto;
            return (
              <div key={msg.id} className={`cb-row cb-row--${msg.role}`}>
                {msg.role === 'agent' && (
                  <div className="cb-avatar" style={{ background: msgMeta.color }}>
                    {msgMeta.emoji}
                  </div>
                )}
                <div
                  className={`cb-bubble cb-bubble--${msg.role}`}
                  style={msg.role === 'agent' ? { borderLeftColor: msgMeta.color } : {}}
                >
                  {msg.role === 'agent' && (
                    <div className="cb-bubble-meta">
                      <span className="cb-bubble-agent" style={{ color: msgMeta.color }}>{msgMeta.name}</span>
                      <span className="cb-bubble-time">
                        {msg.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  )}
                  <div className="cb-bubble-content">
                    {msg.role === 'agent' ? renderMarkdown(msg.content) : msg.content}
                  </div>
                  {msg.hotels && msg.hotels.length > 0 && (
                    <div className="cb-hotel-cards">
                      {msg.hotels.map((h) => (
                        <div
                          key={h.hotel_id}
                          className="cb-hotel-card"
                          onClick={() => navigate(`/property/${h.hotel_id}`)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && navigate(`/property/${h.hotel_id}`)}
                        >
                          <div className="cb-hotel-card-name">{h.name}</div>
                          <div className="cb-hotel-card-meta">
                            {h.category && <span className="cb-hotel-card-cat">{h.category}</span>}
                            {h.city && <span className="cb-hotel-card-city">{h.city}</span>}
                          </div>
                          {h.starting_price_per_night != null && h.starting_price_per_night > 0 && (
                            <div className="cb-hotel-card-price">
                              From PKR {Number(h.starting_price_per_night).toLocaleString()}
                              <span className="cb-hotel-card-night"> / night</span>
                            </div>
                          )}
                          <div className="cb-hotel-card-link">View Hotel →</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {msg.room_types && msg.room_types.length > 0 && (
                    <div className="cb-room-btns">
                      <div className="cb-room-btns-label">Select a room:</div>
                      {msg.room_types.map((rt, i) => (
                        <button
                          key={rt.room_type_id ?? i}
                          className="cb-room-btn"
                          disabled={loading}
                          onClick={() => handleSend(`I'll book the ${rt.name || rt.room_type_id}`)}
                        >
                          <span className="cb-room-btn-name">{rt.name || rt.room_type_id}</span>
                          <span className="cb-room-btn-details">
                            {rt.price_per_night > 0 && (
                              <span className="cb-room-btn-price">PKR {Number(rt.price_per_night).toLocaleString()}/night</span>
                            )}
                            {rt.capacity > 0 && (
                              <span className="cb-room-btn-cap">· Up to {rt.capacity} guests</span>
                            )}
                            {rt.available_rooms > 0 && (
                              <span className="cb-room-btn-avail">· {rt.available_rooms} available</span>
                            )}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {msg.escalation && (
                    <div className="cb-escalation-badge">⚡ Escalated to Manager</div>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="cb-avatar cb-avatar--user">
                    {userProfile?.fullName?.[0]?.toUpperCase() || '👤'}
                  </div>
                )}
              </div>
            );
          })}

          {loading && (
            <div className="cb-row cb-row--agent">
              <div className="cb-avatar" style={{ background: meta.color }}>{meta.emoji}</div>
              <div className="cb-bubble cb-bubble--agent" style={{ borderLeftColor: meta.color }}>
                <div className="cb-typing"><span /><span /><span /></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="cb-input-bar">
          <input
            ref={inputRef}
            className="cb-input"
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder={loading ? 'Waiting…' : 'Type a message…'}
            disabled={loading}
          />
          <button
            className="cb-send-btn"
            onClick={handleSend}
            disabled={loading || !input.trim()}
            style={{ background: meta.color }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <line x1="22" y1="2" x2="11" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* ── FAB trigger button ─────────────────────────────────────────────── */}
      <button
        className={`cb-fab ${open ? 'cb-fab--open' : ''}`}
        style={{ background: meta.color }}
        onClick={() => setOpen(o => !o)}
        title="Chat with Innio"
      >
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        {!open && unread > 0 && (
          <span className="cb-badge">{unread > 9 ? '9+' : unread}</span>
        )}
      </button>
    </>
  );
}
